import React from 'react';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { useEmployeeMapping } from './hooks/useEmployeeMapping';
import { UploadSection } from './components/UploadSection';
import { AlertMessages } from './components/AlertMessages';
import { MappingTable } from './components/MappingTable';
import { ImportModal } from './components/ImportModal';

export const EmployeeMapping: React.FC = () => {
  const {
    // Data
    employees,
    statistics,
    filteredEmployees,
    availableApiKeys,

    // Loading/status
    loading,
    error,
    success,
    importProgress,

    // CSV Import
    showImportModal,
    csvPreview,
    mappings,

    // Filter state
    searchTerm,
    selectedRole,
    showOnlyMapped,

    // Organization filter derived values
    headquarterSelectValue,
    activeHeadquarter,
    departmentSelectValue,
    isDepartmentSelectionDisabled,
    headquarterOptions,
    departmentOptions,

    // API Key selection state
    selectedApiKey,

    // Refs
    fileInputRef,

    // Actions
    setError,
    setSuccess,
    setSearchTerm,
    setSelectedRole,
    setShowOnlyMapped,
    setSelectedApiKey,
    setMappings,
    setShowImportModal,
    setCsvFile,
    setCsvPreview,

    // Handlers
    handleFileSelect,
    handleImport,
    handleAutoMap,
    handleManualMap,
    handleDeleteEmployee,
    handleResetAll,
    handleExport,
    handleExportHtmlReport,
    handleDownloadTemplate,
    handleHeadquarterChange,
    handleDepartmentChange,
  } = useEmployeeMapping();

  if (loading && !showImportModal) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-6">
      {/* Header */}
      <UploadSection
        statistics={statistics}
        employees={employees}
        importProgress={importProgress}
        fileInputRef={fileInputRef}
        onDownloadTemplate={handleDownloadTemplate}
        onFileSelect={handleFileSelect}
        onExport={handleExport}
        onExportHtmlReport={handleExportHtmlReport}
        onAutoMap={handleAutoMap}
        onResetAll={handleResetAll}
      />

      {/* Alerts */}
      <AlertMessages
        error={error}
        success={success}
        onDismissError={() => setError(null)}
        onDismissSuccess={() => setSuccess(null)}
      />

      {/* Filters and Table */}
      <MappingTable
        filteredEmployees={filteredEmployees}
        availableApiKeys={availableApiKeys}
        selectedApiKey={selectedApiKey}
        searchTerm={searchTerm}
        headquarterSelectValue={headquarterSelectValue}
        activeHeadquarter={activeHeadquarter}
        departmentSelectValue={departmentSelectValue}
        isDepartmentSelectionDisabled={isDepartmentSelectionDisabled}
        headquarterOptions={headquarterOptions}
        departmentOptions={departmentOptions}
        selectedRole={selectedRole}
        showOnlyMapped={showOnlyMapped}
        onSearchTermChange={setSearchTerm}
        onHeadquarterChange={handleHeadquarterChange}
        onDepartmentChange={handleDepartmentChange}
        onSelectedRoleChange={setSelectedRole}
        onShowOnlyMappedChange={setShowOnlyMapped}
        onSelectedApiKeyChange={setSelectedApiKey}
        onManualMap={handleManualMap}
        onDeleteEmployee={handleDeleteEmployee}
      />

      {/* Import Modal */}
      {showImportModal && (
        <ImportModal
          csvPreview={csvPreview}
          mappings={mappings}
          loading={loading}
          importProgress={importProgress}
          onMappingsChange={setMappings}
          onImport={handleImport}
          onCancel={() => {
            setShowImportModal(false);
            setCsvFile(null);
            setCsvPreview(null);
          }}
        />
      )}
    </div>
  );
};
